import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useCreateFleetProviderType, useUpdateFleetProviderType, FleetProviderType } from "@/hooks/use-fleet-provider-types";

function generateCodeFromName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .substring(0, 50);
}

interface ProviderTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerType?: FleetProviderType | null;
  companyId: string;
}

export function ProviderTypeModal({ open, onOpenChange, providerType, companyId }: ProviderTypeModalProps) {
  const createMutation = useCreateFleetProviderType(companyId);
  const updateMutation = useUpdateFleetProviderType(companyId);
  
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm({
    defaultValues: {
      name: "",
      code: "",
      description: "",
      icon: "",
      color: "#3b82f6",
      is_active: true,
    }
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (providerType) {
      reset({
        name: providerType.name,
        code: providerType.code,
        description: providerType.description || "",
        icon: providerType.icon || "",
        color: providerType.color || "#3b82f6",
        is_active: providerType.is_active,
      });
    } else {
      reset({
        name: "",
        code: "",
        description: "",
        icon: "",
        color: "#3b82f6",
        is_active: true,
      });
    }
  }, [providerType, reset]);

  useEffect(() => {
    if (!providerType && nameValue) {
      const generatedCode = generateCodeFromName(nameValue);
      setValue("code", generatedCode);
    }
  }, [nameValue, providerType, setValue]);

  const onSubmit = async (data: any) => {
    try {
      if (providerType) {
        await updateMutation.mutateAsync({ id: providerType.id, ...data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onOpenChange(false);
      reset();
    } catch (error) {
      console.error("Error saving provider type:", error);
    }
  };

  const isActive = watch("is_active");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {providerType ? "Editar" : "Novo"} Tipo de Fornecedor
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              {...register("name", { required: "Nome é obrigatório" })}
              placeholder="Ex: Posto de Combustível"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            {!providerType && watch("code") && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Código gerado:</span>
                <Badge variant="outline" className="font-mono">
                  {watch("code")}
                </Badge>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder="Descrição do tipo de fornecedor"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="icon">Ícone (Lucide)</Label>
              <Input
                id="icon"
                {...register("icon")}
                placeholder="Ex: Fuel"
              />
              <p className="text-xs text-muted-foreground">
                Nome do ícone do Lucide React
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Cor</Label>
              <div className="flex gap-2">
                <Input
                  id="color"
                  type="color"
                  {...register("color")}
                  className="w-16 h-10"
                />
                <Input
                  {...register("color")}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="is_active">Tipo Ativo</Label>
            <Switch
              id="is_active"
              checked={isActive}
              onCheckedChange={(checked) => setValue("is_active", checked)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {providerType ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
